import { Checkbox, Col, Row } from 'antd';
import { DateTime } from 'luxon';
import React, { useState } from 'react';

import { translate } from '@portal/services/i18n';
import { moneyFormat } from '@portal/utils/format';
import BankDataForm from '@portal/components/BankDataForm/BankDataForm';
import AdvancedAlert from '@portal/components/AdvancedAlert/AdvancedAlert';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import InstallmentsModal from '@portal/components/InstallmentsModal/InstallmentsModal';

export interface LoanExternalSimulationProps {
  externalSimulation: models.LoanExternalSimulation;
  confirmed: boolean;
  setConfirmed: (value: boolean) => void;
  bankAgency?: string | null;
  setBankAgency?: (value: string) => void;
  bankAccount?: string | null;
  setBankAccount?: (value: string) => void;
  bankAccountDigit?: string | null;
  setBankAccountDigit?: (value: string) => void;
  bankCode?: string | null;
  setBankCode?: (value: string) => void;
}

const LoanExternalSimulation = ({
  externalSimulation,
  confirmed,
  setConfirmed,
  bankAgency,
  setBankAgency,
  bankAccount,
  setBankAccount,
  bankAccountDigit,
  setBankAccountDigit,
  bankCode,
  setBankCode,
}: LoanExternalSimulationProps) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div>
      <h1 className="loan-external-simulation__title">
        {translate('PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.SPECIFICATIONS')}
      </h1>

      <Col className="loan-external-simulation__container" span={32}>
        <div className="loan-external-simulation__container__alert-wrapper">
          <AdvancedAlert>
            {translate('PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.ALERT')}
          </AdvancedAlert>
        </div>
        <Row>
          <Col span={8}>
            <p className="loan-external-simulation__container__subtitle">
              {translate('PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.VALUE')}
            </p>
            <p className="loan-external-simulation__container__title">
              {moneyFormat(externalSimulation.grossAmount)}
            </p>
          </Col>
          <Col span={8}>
            <p className="loan-external-simulation__container__subtitle">
              {translate('PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.VALUE_IOF')}
            </p>
            <p className="loan-external-simulation__container__title">
              {moneyFormat(externalSimulation.totalIOFtax)}
            </p>
          </Col>
          <Col span={8}>
            <p className="loan-external-simulation__container__subtitle">
              {translate(
                'PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.REGISTRATION_FEE'
              )}
            </p>
            <p className="loan-external-simulation__container__title">
              {moneyFormat(externalSimulation.otherDebitsAmount)}{' '}
              {externalSimulation.otherDebitsAmount
                ? `(${externalSimulation.otherDebits} %)`
                : ''}
            </p>
          </Col>
        </Row>
        <Row>
          <Col span={8}>
            <p className="loan-external-simulation__container__subtitle">
              {translate(
                'PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.LIQUID_VALUE'
              )}
            </p>
            <p className="loan-external-simulation__container__title">
              {moneyFormat(externalSimulation.netAmount)}
            </p>
          </Col>
          <Col span={8}>
            <p className="loan-external-simulation__container__subtitle">
              {translate('PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.TAX')}
            </p>
            <p className="loan-external-simulation__container__title">
              {externalSimulation.tax.toFixed(2)} %
            </p>
          </Col>
          <Col span={8}>
            <p className="loan-external-simulation__container__subtitle">
              {translate('PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.TAX_YEAR')}
            </p>
            <p className="loan-external-simulation__container__title">
              {externalSimulation.equivalentTax.toFixed(2)} %
            </p>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col span={8}>
            <p className="loan-external-simulation__container__subtitle">
              {translate('PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.MONTHLY_CET')}
            </p>
            <p className="loan-external-simulation__container__title">
              {externalSimulation.monthlyCETtax.toFixed(2)} %
            </p>
          </Col>
          <Col span={8}>
            <p className="loan-external-simulation__container__subtitle">
              {translate('PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.YEARLY_CET')}
            </p>
            <p className="loan-external-simulation__container__title">
              {externalSimulation.anuallyCETtax.toFixed(2)} %
            </p>
          </Col>
          <Col span={8}>
            <p className="loan-external-simulation__container__subtitle">
              {translate('PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.INDEX')}
            </p>
            <p className="loan-external-simulation__container__title">
              {translate('PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.SELIC')}
            </p>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col span={8}>
            <p className="loan-external-simulation__container__subtitle">
              {translate(
                'PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.FIRST_INSTALLMENTS'
              )}
            </p>
            <p className="loan-external-simulation__container__title">
              {DateTime.fromISO(
                externalSimulation.installmentsData[0]?.dueDate
              ).toFormat(translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON'))}
            </p>
          </Col>
          <Col span={8}>
            <p className="loan-external-simulation__container__subtitle">
              {translate(
                'PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.CONTRACT_START_DATE'
              )}
            </p>
            <p className="loan-external-simulation__container__title">
              {DateTime.fromISO(externalSimulation.contractStartDate).isValid
                ? DateTime.fromISO(
                    externalSimulation.contractStartDate
                  ).toFormat(translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON'))
                : ''}
            </p>
          </Col>
          <AdvancedButton
            text={translate(
              'PAGES.LOAN.SIMULATE.EXTERNAL_SIMULATION.SEE_INSTALLMENTS'
            )}
            onClick={() => setIsVisible(true)}
          />
        </Row>

        <BankDataForm
          bankAgency={bankAgency ?? null}
          setBankAgency={setBankAgency}
          bankAccount={bankAccount ?? null}
          setBankAccount={setBankAccount}
          bankAccountDigit={bankAccountDigit ?? null}
          setBankAccountDigit={setBankAccountDigit}
          bankCode={bankCode ?? null}
          setBankCode={setBankCode}
        />

        <Checkbox
          className="mt-3"
          onChange={(event) => setConfirmed(event.target.checked)}
          checked={confirmed}
        >
          {translate('PAGES.LOAN.SIMULATE.CONFIRM_CONTRACT')}
        </Checkbox>
      </Col>
      <InstallmentsModal
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        installments={externalSimulation.installmentsData}
      />
    </div>
  );
};

export default LoanExternalSimulation;
